/**
 * Superadmin control-plane client (ADR-0036 Phase 2).
 *
 * A SEPARATE realm from the operator app: its own token store (never shares the
 * operator `hub-auth` store) and its own fetch wrapper rooted at `/api/platform`.
 * The hard boundary is enforced server-side by the `realm` JWT claim.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { BillingOverview } from "@/lib/api-billing";
import { shouldRefresh, tokenExpiryMs } from "@/lib/session";
import type { TenantProfile, TenantProfileIn } from "@/lib/tenantProfile";

const BASE = "/api/platform";

export interface PlatformAdmin {
  id: string;
  email: string;
  full_name: string | null;
}

interface PlatformAuthState {
  token: string | null;
  /** Opaque rotated refresh token — renews the short access token silently so the
   *  superadmin session doesn't just expire (aligned with the operator realm). */
  refreshToken: string | null;
  admin: PlatformAdmin | null;
  setSession: (token: string, admin: PlatformAdmin, refreshToken?: string | null) => void;
  logout: () => void;
}

export const usePlatformAuth = create<PlatformAuthState>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      admin: null,
      setSession: (token, admin, refreshToken = null) => set({ token, admin, refreshToken }),
      logout: () => set({ token: null, admin: null, refreshToken: null }),
    }),
    { name: "tray-platform-auth" },
  ),
);

// Single-flight: concurrent platform calls share one refresh request.
let platformRefreshInFlight: Promise<void> | null = null;

/**
 * Refresh the platform access token before it expires (mirror of
 * `lib/session.ts::maybeRefreshSession` for the superadmin realm). No-op without a
 * refresh token or while the access token has plenty of life; a rejected refresh
 * logs the session out. A network error is swallowed (the call proceeds with the
 * old token). Called before every platform API request.
 */
export async function maybeRefreshPlatformSession(): Promise<void> {
  const { token, refreshToken, admin } = usePlatformAuth.getState();
  if (!token || !admin || !refreshToken) return;
  const exp = tokenExpiryMs(token);
  if (exp === null || !shouldRefresh(exp, Date.now())) return;

  platformRefreshInFlight ??= fetch(BASE + "/auth/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
    .then(async (r) => {
      const state = usePlatformAuth.getState();
      if (state.refreshToken !== refreshToken || !state.admin) return; // a logout raced us
      if (r.ok) {
        const data = (await r.json()) as { token?: string; refresh_token?: string };
        if (data.token && data.refresh_token) {
          state.setSession(data.token, state.admin, data.refresh_token);
        }
      } else if (r.status === 401 || r.status === 403) {
        state.logout();
      }
    })
    .catch(() => undefined)
    .finally(() => {
      platformRefreshInFlight = null;
    });
  await platformRefreshInFlight;
}

/**
 * Voluntary logout: retire the refresh-token family server-side (best-effort), then
 * clear the store — parity with the operator `endSession`. The 401/403 paths use the
 * bare store `logout()` (a dead session has nothing to revoke).
 */
export async function endPlatformSession(): Promise<void> {
  const { refreshToken } = usePlatformAuth.getState();
  if (refreshToken) {
    try {
      await fetch(BASE + "/auth/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch {
      // Offline logout is still a logout.
    }
  }
  usePlatformAuth.getState().logout();
}

export class PlatformApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "PlatformApiError";
  }
}

/** Extract a READABLE message from a hub error body. The hub shape is
 * `{error: {code, message}}` — passing `body.error` (an OBJECT) into `new Error(...)`
 * rendered as "[object Object]" in every platform dialog (the Licenze mint bug). */
export function platformErrorMessage(body: unknown, fallback: string): string {
  const b = body as { detail?: unknown; error?: unknown } | null;
  const err = b?.error;
  if (err && typeof err === "object" && typeof (err as { message?: unknown }).message === "string")
    return (err as { message: string }).message;
  if (typeof b?.detail === "string") return b.detail;
  if (typeof err === "string") return err;
  return fallback;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  await maybeRefreshPlatformSession();
  const token = usePlatformAuth.getState().token;
  const resp = await fetch(BASE + path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers || {}),
    },
  });
  if (resp.status === 401 || resp.status === 403) {
    // The platform session is gone / not a platform token — drop it.
    usePlatformAuth.getState().logout();
  }
  const text = await resp.text();
  const body = text ? JSON.parse(text) : null;
  if (!resp.ok) {
    throw new PlatformApiError(resp.status, platformErrorMessage(body, resp.statusText));
  }
  return body as T;
}

/** Multipart POST for a logo upload — NO JSON Content-Type so the browser sets the
 * multipart boundary. Mirrors `request`'s auth + error handling. */
async function uploadLogoRequest(path: string, file: File): Promise<TenantProfile> {
  await maybeRefreshPlatformSession();
  const token = usePlatformAuth.getState().token;
  const fd = new FormData();
  fd.append("file", file);
  const resp = await fetch(BASE + path, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (resp.status === 401 || resp.status === 403) usePlatformAuth.getState().logout();
  const text = await resp.text();
  const body = text ? JSON.parse(text) : null;
  if (!resp.ok) {
    throw new PlatformApiError(resp.status, platformErrorMessage(body, resp.statusText));
  }
  return body as TenantProfile;
}

export type TenantStatus = "active" | "suspended" | "provisioning";
export type TenantPlan = "single" | "multi";

export interface Tenant {
  id: string;
  slug: string;
  schema_name: string;
  name: string | null;
  status: TenantStatus;
  plan: TenantPlan;
  cell_id?: string | null;
  created_at: string;
}

/** A deployment shard (cell) of the SaaS fleet (ADR-0074 Phase 3). */
/**
 * Il piano PARTNER (ADR-0081). Un partner è una SOCIETÀ rivenditrice; le persone che
 * accedono a Tray Central sono `PartnerUser`, e il legame con un cliente è un ARCO
 * (`PartnerOrganization`) che nasce da una rivendicazione approvata.
 *
 * Da tenere a mente leggendo questi tipi: **approvare un arco non apre nessun accesso.**
 * È attribuzione commerciale; per entrare in un'istanza servono il consenso del cliente e
 * il codice memorizzato che combacia, verificati IN LOCALE dall'istanza (D5).
 */
export interface Partner {
  id: string;
  /** L'IDENTIFICATIVO, non un'etichetta: è la stringa che l'istanza del cliente confronta
   *  col permesso firmato. Per questo non è modificabile — rinominarla revocherebbe ogni
   *  accesso in essere. Per togliere l'accesso c'è `enabled`. */
  code: string;
  name: string;
  contact_email: string | null;
  enabled: boolean;
  created_at: string;
  /** Quanti clienti tiene oggi — il numero che rende la lista leggibile senza aprire ogni riga. */
  organization_count: number;
}

/**
 * Un ARCO, appiattito con la persona che indica (ADR-0082 D2).
 *
 * `id` è il CAPPELLO, `identity_id` è la PERSONA: tenerli distinti è il punto della
 * fetta — togliere il cappello non tocca l'account, i suoi workspace o la sua storia.
 * Non esiste più un `PartnerUser`: il realm partner è stato ritirato, quindi qui non
 * c'è niente da abilitare, nessuna password e nessun `last_login` — la sessione la
 * governa l'Account Tray.
 */
export interface PartnerMember {
  /** L'arco. È questo che si toglie, ed è questo che le rotte `/partner-members/:id` indirizzano. */
  id: string;
  partner_id: string;
  /** La persona. Vive su `identities`, sopravvive alla revoca del cappello. */
  identity_id: string;
  email: string;
  full_name: string | null;
  role: PartnerMemberRole;
  /** Lo stato dell'ACCOUNT: un'identità disabilitata non indossa alcun cappello. */
  state: string;
  email_verified: boolean;
  /** Ha già scelto una password (invito raccolto) oppure l'invito è ancora in volo. */
  claimed: boolean;
  created_at: string;
}

export type PartnerMemberRole = "admin" | "technician";

/**
 * L'esito dell'aggiunta. `claim_url` è la GARANZIA, la mail è la comodità: con
 * `mail_sent: false` il superadmin copia comunque il link e lo passa al rivenditore.
 */
export interface PartnerMemberAdded {
  member: PartnerMember;
  /** `linked` = l'Account Tray esisteva già; `invited` = è stato creato e deve raccoglierlo. */
  mode: "linked" | "invited";
  claim_url: string | null;
  login_url: string | null;
  mail_sent: boolean;
}

/** Un arco approvato: questo partner tiene questo cliente. */
export interface PartnerArc {
  id: string;
  partner_id: string;
  organization_id: string;
  created_at: string;
  organization_name: string | null;
}

/** Una richiesta in coda. `partner_name`/`organization_name` arrivano denormalizzati:
 *  una claim resa come due UUID è illeggibile. */
export interface PartnerClaim {
  id: string;
  partner_id: string;
  organization_id: string;
  source: string;
  status: string;
  origin_ref: string | null;
  decided_by: string | null;
  decided_at: string | null;
  note: string | null;
  created_at: string;
  partner_name: string | null;
  partner_code: string | null;
  organization_name: string | null;
}

export interface PlatformCell {
  id: string;
  name: string;
  endpoint: string;
  region: string | null;
  status: "active" | "draining" | "disabled";
  is_default: boolean;
  created_at: string;
  tenant_count: number;
}

/**
 * A COMMERCIAL organization (ADR-0074 "modello Organizzazione") — the anagrafica that resolves the
 * tenant↔organizzazione collision. A Organizzazione owns 1..N tenants (cloud workspaces) AND 1..N
 * on-prem licenses, so "what does this company have with us?" is one record instead of
 * two unrelated lists cross-referenced by name.
 */
/** Why a organization is suspected to duplicate another (ADR-0076 decision 9). Only `vat`
 * today — the email-domain signal was dropped because it flagged every pair of companies
 * founded by the same person. Detection only — the cure is move + archive, never an
 * automatic merge. Kept as a union so a real new signal can join without a wire change. */
export interface OrganizationDuplicate {
  kind: "vat";
  organization_id: string;
  name: string;
}

export interface PlatformOrganization {
  id: string;
  name: string;
  legal_name: string | null;
  vat_number: string | null;
  tax_code: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  /** Italian e-invoicing pair. Both are RE-DERIVED server-side from (country, entity_type),
   * so they are read-only here: the form is not the source of truth for that rule. */
  pec: string | null;
  sdi_code: string | null;
  notes: string | null;
  status: "active" | "archived";
  created_at: string;
  tenant_count: number;
  license_count: number;
  /** Filled by the LIST route only (empty elsewhere). */
  duplicates?: OrganizationDuplicate[];
}

/** The detail view: the organization PLUS everything it holds, on one screen. */
export interface PlatformOrganizationDetail extends PlatformOrganization {
  tenants: { id: string; slug: string; name: string | null; status: string; schema_name: string }[];
  licenses: {
    id: string;
    activation_key_prefix: string;
    status: string;
    expires_at: string;
    installation_id: string | null;
    heartbeat_at: string | null;
    /** What this install reports about itself (ADR-0074 L1) — see `PlatformLicense`. */
    last_report: LicenseReport;
    last_report_at: string | null;
  }[];
}

/** A person attached to an ORGANIZATION (ADR-0078 slice 1) — the second arc.
 *  `role`: `admin` may act on the organization itself, `member` just belongs to it.
 *  It says nothing about workspace access: that is `identity_memberships`, and the two
 *  are deliberately independent (the consultant is admin of one org and a guest in
 *  another org's workspace). */
export interface OrgMember {
  identity_id: string;
  email: string;
  full_name: string | null;
  role: "admin" | "member";
  /** The IDENTITY's global state — a disabled person still holds the arc. */
  identity_state: string;
}

/**
 * What adding a person to an organization did — the SAME shape the partner and staff
 * arcs return, because they are the same act (ADR-0082 D8).
 *
 * `mode` is `linked` when the address already had an Account Tray (nothing to claim)
 * and `invited` when one was created (or was still unclaimed), in which case
 * `claim_url` is the guarantee: the mail is the convenience, the copyable link is what
 * the superadmin can always fall back on. `members` is the refreshed roster.
 */
export interface OrgMemberAdded {
  members: OrgMember[];
  mode: string;
  claim_url: string | null;
  login_url: string | null;
  mail_sent: boolean;
}

/** The editable anagrafica fields — shared by create and PATCH. */
export interface PlatformOrganizationIn {
  name?: string;
  legal_name?: string | null;
  vat_number?: string | null;
  tax_code?: string | null;
  email?: string | null;
  phone?: string | null;
  country?: string | null;
  notes?: string | null;
  status?: "active" | "archived";
}

/** An on-prem license key (ADR-0073) — one DEPLOYMENT of a `PlatformOrganization`. */
/** One EMISSION of a license (ADR-0076 decision 8) — a row of the history tab. */
export interface LicenseIssuance {
  id: string;
  reason: "initial" | "renewal" | "amendment";
  billing_period: "monthly" | "annual" | "custom";
  starts_at: string;
  ends_at: string;
  grace_days: number;
  entitlements: Record<string, unknown>;
  supersedes_id: string | null;
  amount_cents: number | null;
  currency: string | null;
  paid_at: string | null;
  payment_ref: string | null;
  notes: string | null;
  issued_by: string | null;
  issued_at: string;
}

export interface LicenseIssuanceIn {
  reason: "renewal" | "amendment";
  billing_period?: "monthly" | "annual" | "custom";
  starts_at?: string | null;
  ends_at?: string | null;
  grace_days?: number | null;
  entitlements?: Record<string, unknown> | null;
  amount_cents?: number | null;
  currency?: string | null;
  paid_at?: string | null;
  payment_ref?: string | null;
  notes?: string | null;
}

/**
 * A repair of the emission IN FORCE. Every field optional and only what is SENT changes —
 * fixing an end date must not blank the payment reference nobody mentioned. `entitlements`
 * is absent by design: that is the DEAL, and it goes through an amendment.
 */
export interface LicenseIssuanceCorrection {
  starts_at?: string;
  ends_at?: string;
  grace_days?: number;
  amount_cents?: number | null;
  currency?: string | null;
  paid_at?: string | null;
  payment_ref?: string | null;
  notes?: string | null;
}

/** Account Tray identity (ADR-0077 S4) — the offboarding console rows. */
export interface PlatformIdentityMembership {
  tenant_id: string;
  slug: string;
  name: string | null;
  status: string;
}

export interface PlatformIdentity {
  id: string;
  email: string;
  full_name: string | null;
  state: string;
  /** false = invited-but-unclaimed (no password yet) — "Invito in sospeso",
   *  not a disabled account. */
  claimed: boolean;
  email_verified: boolean;
  last_login_at: string | null;
  created_at: string;
  memberships: PlatformIdentityMembership[];
}

export interface PlatformIdentityList {
  items: PlatformIdentity[];
  total: number;
}

export interface PlatformLicense {
  id: string;
  activation_key_prefix: string;
  organization: string;
  organization_id: string | null;
  status: string;
  installation_id: string | null;
  /** PROJECTED from the current issuance, which is where validity now lives (ADR-0076
   * decision 8). `expires_at` is null only for a key whose ledger is empty. */
  entitlements: Record<string, unknown>;
  expires_at: string | null;
  grace_days: number;
  current_issuance: LicenseIssuance | null;
  issuance_count: number;
  heartbeat_at: string | null;
  activated_at: string | null;
  created_at: string;
  /**
   * What the install told us about itself on its last heartbeat (ADR-0074 L1) — its
   * running version + usage COUNTS, never names or rows. `{}` = it has never reported
   * (a hub older than L1, or one whose best-effort collection failed).
   */
  last_report: LicenseReport;
  last_report_at: string | null;
}

/** The on-prem self-report. Both halves are optional — a hub that could collect only its
 * version still reports, rather than failing its heartbeat over telemetry. */
export interface LicenseReport {
  version?: string;
  counts?: Record<string, number>;
}

/** The mint response — `activation_key` is shown ONCE, never retrievable again. */
export interface PlatformLicenseMint extends PlatformLicense {
  activation_key: string;
}

export interface TenantDetail extends Tenant {
  table_count: number;
  user_count: number;
  /** The COMMERCIAL organization this deployment belongs to (ADR-0076) — null = unattached.
   * `organization_name` rides along so the card renders without a second round-trip. */
  organization_id: string | null;
  organization_name: string | null;
}

export interface TenantStats {
  total: number;
  active: number;
}

/** Tenant health snapshot (ADR-0036 diagnostics): migration drift + schema completeness
 * + (full only) data footprint. `footprint` is omitted on the overview board. */
/** A tenant's derived billing state for the control-plane list + Dashboard (ADR-0041 §13).
 * `state`: active | past_due | cancel_scheduled | canceled | none (none = comp / on-prem). */
export interface TenantBillingRow {
  tenant_id: string;
  slug: string;
  state: "active" | "past_due" | "cancel_scheduled" | "canceled" | "none";
  plan_key: string | null;
  plan_name: string | null;
  billing_period: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  past_due_at: string | null;
  billing_suspended: boolean;
}

export interface TenantHealth {
  tenant_id: string;
  slug: string;
  schema_name: string;
  status: TenantStatus;
  /** False on the SPLIT Control: the tenant's schema lives on its CELL's DB, so
   * migration/schema are null and `ok` carries no verdict — diagnostics run there. */
  hosted_here: boolean;
  migration: { current: string | null; head: string | null; behind: boolean } | null;
  schema: {
    table_count: number;
    expected_count: number;
    missing_tables: string[];
    extra_tables: string[];
    complete: boolean;
  } | null;
  ok: boolean | null;
  footprint?: {
    users: number | null;
    active_users: number | null;
    groups: number | null;
    apps: number | null;
    workflows: number | null;
    knowledge_bases: number | null;
    kb_vector_tables: number;
  };
}

export interface MigrateResult {
  ok: boolean;
  current?: string | null;
  error: string | null;
}

/** The runtime-tunable global concurrency-cap defaults (ADR-0040). */
export interface PlatformSettingsCaps {
  max_concurrent_workflow_runs: number;
  max_concurrent_etl_runs: number;
}

/** Platform SMTP relay (ADR-0077 decision 7) — invites + self-service reset. The password
 * is WRITE-ONLY (vault): the wire carries only whether one is stored. */
export interface PlatformSmtpConfig {
  smtp_host: string;
  smtp_port: number;
  smtp_tls: "starttls" | "ssl" | "none";
  smtp_username: string;
  smtp_from: string;
}

export interface PlatformSettings extends PlatformSettingsCaps, PlatformSmtpConfig {
  smtp_password_set: boolean;
  /** The code-level fail-safe each cap falls back to when unset (so the UI can show it). */
  defaults: PlatformSettingsCaps;
}

/** Sparse PUT body: caps + SMTP config + the write-only password ("" clears it). */
export type PlatformSettingsUpdate = Partial<
  PlatformSettingsCaps & PlatformSmtpConfig & { smtp_password: string }
>;

/** What `resolver.classify_host` decides for a host (routing debug). */
export interface ResolveResult {
  host: string;
  base_hostname: string;
  classification: "default" | "platform" | "onboarding" | "tenant" | "suspended" | "unknown";
  slug: string | null;
  schema_name: string | null;
}

/** Polymorphic login result (F2.0): `ok` carries the session; `totp_required` a
 * challenge token (exchange with a code); `totp_setup_required` a setup token (enforce). */
export interface PlatformLoginResult {
  status: "ok" | "totp_required" | "totp_setup_required";
  token: string | null;
  refresh_token?: string | null;
  admin: PlatformAdmin | null;
  challenge_token: string | null;
  setup_token: string | null;
}

export const platformApi = {
  login: (email: string, password: string) =>
    request<PlatformLoginResult>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  loginTotp: (challenge_token: string, code: string) =>
    request<PlatformLoginResult>("/auth/login/totp", {
      method: "POST",
      body: JSON.stringify({ challenge_token, code }),
    }),
  me: () => request<PlatformAdmin>("/auth/me"),
  listTenants: () => request<Tenant[]>("/tenants"),
  stats: () => request<TenantStats>("/tenants/stats"),
  getTenant: (id: string) => request<TenantDetail>(`/tenants/${id}`),
  createTenant: (body: {
    slug: string;
    name?: string | null;
    plan?: TenantPlan;
    // The VIP/enterprise flow: cell + billing plan chosen AT creation (a later cell
    // move is a data migration, so this is the one moment the choice happens).
    cell_id?: string | null;
    plan_key?: string | null;
    billing_period?: "month" | "year";
    // The COMMERCIAL organization (ADR-0074 "modello Organizzazione"). An explicit id WINS;
    // `organization_name` creates-or-reuses one by name (onboard a new company inline).
    organization_id?: string | null;
    organization_name?: string | null;
  }) => request<Tenant>("/tenants", { method: "POST", body: JSON.stringify(body) }),
  // Cells (ADR-0074 Phase 3) — the deployment-shard registry.
  listCells: () => request<PlatformCell[]>("/cells"),
  createCell: (body: {
    name: string;
    endpoint: string;
    region?: string | null;
    status?: PlatformCell["status"];
    is_default?: boolean;
  }) => request<PlatformCell>("/cells", { method: "POST", body: JSON.stringify(body) }),
  updateCell: (
    id: string,
    body: {
      name?: string;
      endpoint?: string;
      region?: string | null;
      status?: PlatformCell["status"];
    },
  ) => request<PlatformCell>(`/cells/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  setDefaultCell: (id: string) =>
    request<PlatformCell>(`/cells/${id}/set-default`, { method: "POST" }),
  deleteCell: (id: string) => request<void>(`/cells/${id}`, { method: "DELETE" }),
  // Mint/ROTATE the cell's own backbone pull credential — plaintext shown ONCE (hash
  // stored); goes into the cell's deploy env as its BACKBONE_FLEET_TOKEN.
  mintCellBackboneToken: (id: string) =>
    request<{ token: string }>(`/cells/${id}/backbone-token`, { method: "POST" }),
  // Organizzazioni (ADR-0074 "modello Organizzazione") — the commercial anagrafica.
  listOrganizations: (params?: { search?: string; status?: "active" | "archived" }) => {
    const q = new URLSearchParams();
    if (params?.search) q.set("search", params.search);
    if (params?.status) q.set("status", params.status);
    const qs = q.toString();
    return request<PlatformOrganization[]>(`/organizations${qs ? `?${qs}` : ""}`);
  },
  getOrganization: (id: string) => request<PlatformOrganizationDetail>(`/organizations/${id}`),
  createOrganization: (body: PlatformOrganizationIn) =>
    request<PlatformOrganization>("/organizations", { method: "POST", body: JSON.stringify(body) }),
  updateOrganization: (id: string, body: PlatformOrganizationIn) =>
    request<PlatformOrganization>(`/organizations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  // Deletes the COMMERCIAL record only — its tenants + licenses survive, detached
  // (`ON DELETE SET NULL`). Use `status: "archived"` to retire one keeping the link.
  deleteOrganization: (id: string) => request<void>(`/organizations/${id}`, { method: "DELETE" }),

  // --- organization members (ADR-0078 slice 1) ------------------------------
  // Where the FIRST admin of an existing Organizzazione is appointed. Every mutation
  // returns the refreshed list, so the caller never re-fetches.
  listOrgMembers: (organizationId: string) =>
    request<OrgMember[]>(`/organizations/${organizationId}/members`),
  // Link-or-INVITE: an unknown address gets an Account Tray created for it (it used to
  // be refused). So this one returns the outcome + the roster, not just the roster.
  addOrgMember: (
    organizationId: string,
    body: {
      email: string;
      role: "admin" | "member";
      // Optional, and only a STARTING name: the person renames itself from its own
      // Tray Account. It exists so a fresh invite isn't a bare address in the roster.
      full_name?: string | null;
      send_mail?: boolean;
    },
  ) =>
    request<OrgMemberAdded>(`/organizations/${organizationId}/members`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  setOrgMemberRole: (organizationId: string, identityId: string, role: "admin" | "member") =>
    request<OrgMember[]>(`/organizations/${organizationId}/members/${identityId}`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    }),
  removeOrgMember: (organizationId: string, identityId: string) =>
    request<OrgMember[]>(`/organizations/${organizationId}/members/${identityId}`, {
      method: "DELETE",
    }),
  // The same route attaches AND detaches — `organization_id: null` unlinks.
  setTenantOrganization: (tenantId: string, organizationId: string | null) =>
    request<unknown>(`/tenants/${tenantId}/organization`, {
      method: "POST",
      body: JSON.stringify({ organization_id: organizationId }),
    }),
  // The license LEDGER (ADR-0076 decision 8) — renewal and amendment are the SAME
  // endpoint, discriminated by `reason`. The activation key is never re-issued.
  listLicenseIssuances: (licenseId: string) =>
    request<LicenseIssuance[]>(`/licenses/${licenseId}/issuances`),
  createLicenseIssuance: (licenseId: string, body: LicenseIssuanceIn) =>
    request<PlatformLicense>(`/licenses/${licenseId}/issuances`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // A CORRECTION, not an emission: it repairs the issuance IN FORCE in place (a mistyped
  // expiry, a missing payment reference) and does NOT grow the ledger — nobody bought
  // anything. Changing what was SOLD stays an amendment, which leaves its own row.
  correctLicenseIssuance: (
    licenseId: string,
    issuanceId: string,
    body: LicenseIssuanceCorrection,
  ) =>
    request<PlatformLicense>(`/licenses/${licenseId}/issuances/${issuanceId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  setLicenseOrganization: (licenseId: string, organizationId: string | null) =>
    request<unknown>(`/licenses/${licenseId}/organization`, {
      method: "POST",
      body: JSON.stringify({ organization_id: organizationId }),
    }),
  // On-prem licenses (ADR-0073) — mint (key shown ONCE) / list / revoke.
  listLicenses: () => request<PlatformLicense[]>("/licenses"),

  // --- Account Tray identities (ADR-0077 S4): the offboarding console ---
  listIdentities: (params?: { q?: string; page?: number; page_size?: number }) => {
    const q = new URLSearchParams();
    if (params?.q) q.set("q", params.q);
    if (params?.page) q.set("page", String(params.page));
    if (params?.page_size) q.set("page_size", String(params.page_size));
    const qs = q.toString();
    return request<PlatformIdentityList>(`/identities${qs ? `?${qs}` : ""}`);
  },
  /** One-action offboarding: central sessions die now + every membership
   *  workspace disables and kicks out the linked operator row. */
  disableIdentity: (id: string) =>
    request<PlatformIdentity>(`/identities/${id}/disable`, { method: "POST" }),
  enableIdentity: (id: string) =>
    request<PlatformIdentity>(`/identities/${id}/enable`, { method: "POST" }),
  removeIdentityMembership: (id: string, tenantId: string) =>
    request<PlatformIdentity>(`/identities/${id}/memberships/${tenantId}`, {
      method: "DELETE",
    }),
  mintLicense: (body: {
    organization: string;
    // Omit to CREATE-OR-REUSE an anagrafica named after `organization`; pass an id to
    // attach this deployment to an EXISTING organization (the second-install case).
    organization_id?: string | null;
    entitlements: Record<string, unknown>;
    // Per-licence deviations from the plan snapshot — the plan is a TEMPLATE. Only the
    // axes that actually differ belong here (see `lib/licenseEntitlements.diffOverrides`).
    overrides?: Record<string, unknown> | null;
    // The window: start + cadence in, end DERIVED by the issuer. `expires_at` is only
    // required for a `custom` period, which has no derivation.
    starts_at?: string | null;
    expires_at?: string | null;
    billing_period?: "monthly" | "annual" | "custom";
    grace_days?: number;
  }) => request<PlatformLicenseMint>("/licenses", { method: "POST", body: JSON.stringify(body) }),
  revokeLicense: (id: string) =>
    request<PlatformLicense>(`/licenses/${id}/revoke`, { method: "POST" }),
  // Unbind a licence from a DEAD installation so the same key can re-activate on a new box
  // (a rebuilt VM, a `down -v`). NOT a revoke: the licence stays active.
  releaseLicenseInstallation: (id: string) =>
    request<PlatformLicense>(`/licenses/${id}/release-installation`, { method: "POST" }),
  setTenantStatus: (id: string, status: "active" | "suspended") =>
    request<Tenant>(`/tenants/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }),
  deleteTenant: (id: string) => request<void>(`/tenants/${id}`, { method: "DELETE" }),
  // Tenant profile (ADR-0036 Fase 2) — billing/anagrafica + logo, any tenant.
  getTenantProfile: (id: string) => request<TenantProfile>(`/tenants/${id}/profile`),
  updateTenantProfile: (id: string, body: TenantProfileIn) =>
    request<TenantProfile>(`/tenants/${id}/profile`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  uploadTenantLogo: (id: string, file: File) => uploadLogoRequest(`/tenants/${id}/logo`, file),
  deleteTenantLogo: (id: string) =>
    request<TenantProfile>(`/tenants/${id}/logo`, { method: "DELETE" }),
  // Per-tenant billing board (ADR-0041 §13) — the list's Abbonamento column + Dashboard counts.
  tenantsBillingOverview: () => request<TenantBillingRow[]>("/billing/tenants"),
  // Diagnostics (ADR-0036): migration drift + schema completeness + resolver debug.
  tenantsOverview: () => request<TenantHealth[]>("/tenants/overview"),
  tenantHealth: (id: string) => request<TenantHealth>(`/tenants/${id}/health`),
  migrateTenant: (id: string) =>
    request<MigrateResult>(`/tenants/${id}/migrate`, { method: "POST" }),
  migrateAllTenants: () => request<MigrateResult>("/tenants/migrate-all", { method: "POST" }),
  resolveHost: (host: string) =>
    request<ResolveResult>(`/resolve?host=${encodeURIComponent(host)}`),
  listAudit: (params: { event_type?: string; page?: number; page_size?: number }) => {
    const qs = new URLSearchParams();
    if (params.event_type) qs.set("event_type", params.event_type);
    qs.set("page", String(params.page ?? 1));
    qs.set("page_size", String(params.page_size ?? 25));
    return request<PlatformPage<PlatformAuditEntry>>(`/audit?${qs.toString()}`);
  },
  // 2FA self-service (ADR-0017 seam, F2.0). `bearer` overrides the session token —
  // used by the forced-setup flow (a `totp_setup` token, no session yet).
  totpStatus: () => request<PlatformTotpStatus>("/auth/2fa"),
  totpBegin: (bearer?: string) =>
    request<PlatformTotpBegin>("/auth/2fa/begin", { method: "POST", ...bearerInit(bearer) }),
  totpConfirm: (code: string, bearer?: string) =>
    request<PlatformTotpConfirmed>("/auth/2fa/confirm", {
      method: "POST",
      body: JSON.stringify({ code }),
      ...bearerInit(bearer),
    }),
  totpDisable: (code: string) =>
    request<void>("/auth/2fa/disable", { method: "POST", body: JSON.stringify({ code }) }),
  getTotpPolicy: () => request<{ required: boolean }>("/auth/2fa/policy"),
  setTotpPolicy: (required: boolean) =>
    request<{ required: boolean }>("/auth/2fa/policy", {
      method: "PUT",
      body: JSON.stringify({ required }),
    }),
  // Global control-plane settings (ADR-0040) — the runtime-tunable concurrency-cap defaults.
  getSettings: () => request<PlatformSettings>("/settings"),
  updateSettings: (body: PlatformSettingsUpdate) =>
    request<PlatformSettings>("/settings", { method: "PUT", body: JSON.stringify(body) }),
  // Central's directory service token (ADR-0085) — the hash lives in platform_settings;
  // the plaintext is returned ONCE at mint and never again.
  getCentralToken: () => request<{ configured: boolean }>("/central/service-token"),
  mintCentralToken: () => request<{ token: string }>("/central/service-token", { method: "POST" }),
  revokeCentralToken: () => request<void>("/central/service-token", { method: "DELETE" }),
  // Superadmin CRUD (F2.1).
  listAdmins: () => request<PlatformAdminRow[]>("/admins"),
  createAdmin: (body: { email: string; full_name?: string | null; password: string }) =>
    request<PlatformAdminRow>("/admins", { method: "POST", body: JSON.stringify(body) }),
  updateAdmin: (id: string, body: { state?: "active" | "disabled"; full_name?: string }) =>
    request<PlatformAdminRow>(`/admins/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  resetAdminPassword: (id: string, password: string) =>
    request<void>(`/admins/${id}/reset-password`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  resetAdmin2fa: (id: string) => request<void>(`/admins/${id}/reset-2fa`, { method: "POST" }),
  // Billing — plan CRUD + per-tenant subscription (ADR-0041 §13).
  entitlementsCatalog: () => request<EntitlementsCatalog>("/entitlements-catalog"),
  listPlans: () => request<Plan[]>("/plans"),
  seedDefaultPlans: () => request<{ created: number }>("/plans/seed-defaults", { method: "POST" }),
  createPlan: (body: PlanInput) =>
    request<Plan>("/plans", { method: "POST", body: JSON.stringify(body) }),
  updatePlan: (id: string, body: Partial<PlanInput>) =>
    request<Plan>(`/plans/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deletePlan: (id: string) => request<void>(`/plans/${id}`, { method: "DELETE" }),
  // Provision/reconcile the plan's Stripe Product + per-cadence Prices (Tray→Stripe, ADR-0041
  // Fase 1). Auto-fills the Price IDs from the NET price; idempotent + grandfathering.
  syncPlanStripe: (id: string) =>
    request<PlanStripeSync>(`/plans/${id}/sync-stripe`, { method: "POST" }),
  getSubscription: (tenantId: string) =>
    request<Subscription | null>(`/tenants/${tenantId}/subscription`),
  assignSubscription: (
    tenantId: string,
    planId: string,
    billingPeriod: "month" | "year" = "month",
  ) =>
    request<Subscription>(`/tenants/${tenantId}/subscription`, {
      method: "PUT",
      body: JSON.stringify({ plan_id: planId, billing_period: billingPeriod }),
    }),
  resnapshotSubscription: (tenantId: string) =>
    request<Subscription>(`/tenants/${tenantId}/subscription/resnapshot`, { method: "POST" }),
  /** Per-tenant OPERATIONAL concurrency override (ADR-0040): a concrete int overrides this
   * tenant's cap, `null` clears it (back to plan → global default). Touches only the 2
   * concurrency keys; requires an active subscription. */
  setConcurrencyOverrides: (
    tenantId: string,
    caps: { max_concurrent_workflow_runs?: number | null; max_concurrent_etl_runs?: number | null },
  ) =>
    request<Subscription>(`/tenants/${tenantId}/subscription/overrides`, {
      method: "PUT",
      body: JSON.stringify(caps),
    }),
  // Cancel (churn): schedule the cancellation at period end (Stripe `cancel_at_period_end`) —
  // the tenant keeps working until renewal, then suspends. `cancel:false` undoes it. NOT the
  // comp/UNLIMITED path (that's the DELETE, kept server-side but unwired in the UI).
  cancelSubscription: (tenantId: string, cancel = true) =>
    request<Subscription>(`/tenants/${tenantId}/subscription/cancel`, {
      method: "POST",
      body: JSON.stringify({ cancel }),
    }),
  // Organization-care: pull THIS sub's state from Stripe on demand (manual dunning reconcile).
  reconcileSubscription: (tenantId: string) =>
    request<Subscription | null>(`/tenants/${tenantId}/subscription/reconcile`, { method: "POST" }),
  // PAYMENT history pulled live from Stripe (incassi, NOT fiscal invoices). Durable — survives
  // a tenant deletion. `{stripe:false}` when billing is off; empty for a comp/no-organization.
  billingPayments: (tenantId: string) =>
    request<BillingPaymentsResponse>(`/tenants/${tenantId}/billing/payments`),
  // Subscription-change timeline reconstructed from the durable audit log.
  billingTimeline: (tenantId: string) =>
    request<BillingTimelineEntry[]>(`/tenants/${tenantId}/billing/timeline`),
  // The tenant's consumption (current plan + usage meters), built under the tenant's
  // schema — the SAME shape the tenant sees in its own Abbonamento section (ADR-0041 §11).
  tenantUsage: (tenantId: string) => request<BillingOverview>(`/tenants/${tenantId}/usage`),

  // Platform-wide usage aggregate for the Dashboard (ADR-0041 §10): per-tenant usage
  // counts + summed totals (reuses the same per-tenant usage resolver).
  dashboardUsage: () => request<PlatformUsage>("/dashboard/usage"),

  // Onboarding signups funnel (ADR-0041 Fase 1) — parked paid signups (pay-then-provision).
  signupsSummary: () => request<SignupFunnel>("/onboarding/signups/summary"),
  listSignups: (params: { status?: SignupStatus; page?: number; page_size?: number }) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set("status", params.status);
    qs.set("page", String(params.page ?? 1));
    qs.set("page_size", String(params.page_size ?? 25));
    return request<PlatformPage<PendingSignup>>(`/onboarding/signups?${qs.toString()}`);
  },
  // --- Piano partner (ADR-0081) ---------------------------------------------------
  //
  // Il `code` si sceglie alla creazione e NON si modifica (vedi `Partner.code`): non
  // esiste apposta un campo `code` nella update.
  listPartners: (search?: string) =>
    request<Partner[]>(`/partners${search ? `?search=${encodeURIComponent(search)}` : ""}`),
  createPartner: (body: { code: string; name: string; contact_email?: string | null }) =>
    request<Partner>("/partners", { method: "POST", body: JSON.stringify(body) }),
  updatePartner: (
    id: string,
    body: { name?: string; contact_email?: string | null; enabled?: boolean },
  ) => request<Partner>(`/partners/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  listPartnerOrganizations: (id: string) => request<PartnerArc[]>(`/partners/${id}/organizations`),
  // Attribuzione DIRETTA dal Control: l'admin registra una decisione già presa (un
  // contratto, un cambio di rivenditore, un'installazione precedente al codice). Il
  // backend apre comunque la claim `manual` e la approva, così l'arco tiene la sua
  // provenienza. Riassegnare allo stesso partner è un no-op.
  assignPartnerOrganization: (partnerId: string, organizationId: string) =>
    request<PartnerArc>(`/partners/${partnerId}/organizations`, {
      method: "POST",
      body: JSON.stringify({ organization_id: organizationId }),
    }),
  // Le PERSONE del rivenditore, come ARCHI (ADR-0082 D2). Aggiungere qualcuno non è più
  // "creare un'utenza con una password che comunichiamo noi": è dare il CAPPELLO a un
  // Account Tray — che leghiamo se esiste, altrimenti invitiamo. La credenziale la sceglie
  // la persona, sempre: non ne teniamo mai una per conto suo.
  //
  // Queste rotte sopravvivono a C4 (il rivenditore gestirà il proprio roster da Central)
  // perché rispondono a un'altra domanda: come entra il PRIMO admin, e chi in Tray ha
  // concesso l'accesso — che è esattamente ciò che l'audit deve tenere.
  listPartnerMembers: (id: string) => request<PartnerMember[]>(`/partners/${id}/members`),
  addPartnerMember: (
    id: string,
    body: {
      email: string;
      full_name?: string | null;
      role?: PartnerMemberRole;
      /** `false` quando si vuole consegnare il link a mano invece che via mail. */
      send_mail?: boolean;
    },
  ) =>
    request<PartnerMemberAdded>(`/partners/${id}/members`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /**
   * Ridà il link d'accesso a un arco il cui invito non è mai arrivato (SMTP muto,
   * indirizzo sbagliato, filtro antispam). Prima l'unica strada era togliere l'arco e
   * rimetterlo: un gesto distruttivo per riparare un problema di consegna.
   *
   * `send_mail` è FALSE di default — lo si preme proprio quando la mail è la cosa che ha
   * fallito, quindi rispedirla in automatico ripeterebbe l'errore e nasconderebbe il link.
   * Se la persona ha già una password il backend RIFIUTA di coniare un link
   * (`mode: "linked"`, `claim_url: null`): un claim link è un reset password, e questa
   * console non deve poter prendere possesso di un Account Tray vivo.
   */
  reissuePartnerInvite: (memberId: string, sendMail = false) =>
    request<PartnerMemberAdded>(`/partner-members/${memberId}/reissue-invite`, {
      method: "POST",
      body: JSON.stringify({ send_mail: sendMail }),
    }),
  /** Cambia il RUOLO dell'arco. La persona si modifica dalla console identità: qui si
   *  possiede solo il cappello — ed è la separazione che ADR-0082 ha comprato. */
  setPartnerMemberRole: (memberId: string, role: PartnerMemberRole) =>
    request<PartnerMember>(`/partner-members/${memberId}`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    }),
  /** Toglie il CAPPELLO. L'account resta: un ex tecnico è comunque una persona, e il
   *  vecchio realm — dove si "disabilitava l'utente" — non sapeva dirlo. */
  removePartnerMember: (memberId: string) =>
    request<void>(`/partner-members/${memberId}`, { method: "DELETE" }),
  // La CODA di rivendicazione. `status` filtra; il default lato server è `pending`.
  listPartnerClaims: (status?: string) =>
    request<PartnerClaim[]>(`/partner-claims${status ? `?status=${status}` : ""}`),
  createPartnerClaim: (body: {
    partner_id: string;
    organization_id: string;
    origin_ref?: string | null;
  }) => request<PartnerClaim>("/partner-claims", { method: "POST", body: JSON.stringify(body) }),
  approvePartnerClaim: (id: string) =>
    request<PartnerArc>(`/partner-claims/${id}/approve`, { method: "POST" }),
  rejectPartnerClaim: (id: string, note?: string) =>
    request<PartnerClaim>(`/partner-claims/${id}/reject`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),
  // Sciogliere l'arco: il cliente resta, il partner smette di vederlo.
  revokeOrganizationPartner: (organizationId: string) =>
    request<void>(`/organizations/${organizationId}/partner`, { method: "DELETE" }),

  // HARD-DELETE an abandoned signup (an abandoned checkout) → frees the held slug. Idempotent (204).
  cancelSignup: (id: string) =>
    request<void>(`/onboarding/signups/${id}/cancel`, { method: "POST" }),

  // --- Assistenza Tray: l'arco staff (ADR-0082 D4/D9) -------------------------------
  //
  // «Il roster staff siamo noi»: a differenza di quello partner — che il rivenditore si
  // gestisce da Central dopo che nominiamo il primo admin — questo non ha un gemello
  // autogestito. Ogni arco si concede DA QUI, e l'audit dice chi l'ha concesso.
  //
  // Quattro rotte e nessuna PATCH: l'arco non ha niente da modificare (né ruolo né
  // interruttore — vedi `hub.platform.staff_members` per perché entrambe le assenze sono
  // volute). Sei staff o non lo sei, e smettere di esserlo è una DELETE.
  listStaff: () => request<StaffMember[]>("/staff"),
  addStaffMember: (body: { email: string; full_name?: string | null; send_mail?: boolean }) =>
    request<StaffMemberAdded>("/staff", { method: "POST", body: JSON.stringify(body) }),
  /** Ridà il link a un invito mai arrivato. Stesse regole del gemello partner: `send_mail`
   *  è FALSE di default (lo si preme quando è la mail ad aver fallito), e per chi ha già
   *  una password il backend RIFIUTA di coniare un link — un claim link è un reset. */
  reissueStaffInvite: (memberId: string, sendMail = false) =>
    request<StaffMemberAdded>(`/staff/${memberId}/reissue-invite`, {
      method: "POST",
      body: JSON.stringify({ send_mail: sendMail }),
    }),
  /** Toglie il CAPPELLO, non la persona: l'Account Tray resta, con i suoi workspace. */
  removeStaffMember: (memberId: string) =>
    request<void>(`/staff/${memberId}`, { method: "DELETE" }),
};

/**
 * Un arco staff, appiattito con la persona che indica (ADR-0082 D4).
 *
 * È il più SOTTILE dei tre archi, e le assenze sono la decisione: **nessun ruolo** (una
 * gerarchia di supporto è una proprietà della CODA — chi ha in carico un ticket, chi può
 * scalare — e messa qui diventerebbe un potere globale) e **nessun flag `enabled`** (sopra
 * lo staff non c'è una società, quindi «non lavora più qui» È togliere l'arco). Quello che
 * resta è `state`, che appartiene alla PERSONA: un account disabilitato non indossa alcun
 * cappello.
 */
export interface StaffMember {
  /** L'arco — è questo che indirizzano `/staff/:id`. */
  id: string;
  /** La persona. Vive su `identities` e sopravvive alla revoca del cappello. */
  identity_id: string;
  email: string;
  full_name: string | null;
  state: string;
  email_verified: boolean;
  /** Ha già scelto una password, oppure l'invito è ancora in volo. */
  claimed: boolean;
  created_at: string;
}

/** L'esito dell'aggiunta. Come per il partner: `claim_url` è la GARANZIA, la mail è la
 *  comodità — con `mail_sent: false` il link si copia e si passa a mano. */
export interface StaffMemberAdded {
  member: StaffMember;
  /** `linked` = l'Account Tray esisteva già; `invited` = è stato creato e deve raccoglierlo. */
  mode: "linked" | "invited";
  claim_url: string | null;
  login_url: string | null;
  mail_sent: boolean;
}

/** A tenant's usage counts (per LIMIT axis key) for the platform aggregate board. */
export interface PlatformUsageRow {
  tenant_id: string;
  slug: string;
  name: string | null;
  schema_name: string;
  usage: Record<string, number>;
}

export interface PlatformUsage {
  totals: Record<string, number>;
  tenants: PlatformUsageRow[];
}

/** A parked paid signup's lifecycle status (the onboarding funnel). */
export type SignupStatus = "pending" | "completed" | "expired" | "canceled";

/** Counts by status — drives the funnel cards. */
export interface SignupFunnel {
  pending: number;
  completed: number;
  expired: number;
  canceled: number;
}

/** A parked signup row (pay-then-provision). The hashed password is NEVER serialised. */
export interface PendingSignup {
  id: string;
  /** `signup` = a new company; `workspace` = an extra one for an existing organization. */
  kind: "signup" | "workspace";
  slug: string;
  company_name: string;
  admin_email: string;
  status: SignupStatus;
  billing_period: string;
  plan_key: string | null;
  stripe_customer_id: string | null;
  stripe_checkout_session_id: string | null;
  provisioned_tenant_id: string | null;
  error: string | null;
  created_at: string;
  expires_at: string;
}

/** An entitlement AXIS (from the code catalog) — drives the plan FORM (ADR-0041). */
export interface EntitlementAxis {
  key: string;
  label: string;
  description: string;
  kind?: string | null; // limits only: "count" | "volume"
  default: boolean | number | null;
  /** limits only — what an UNSET value means: "unlimited" (default) or "global" (the
   * concurrency caps inherit the global default, NOT unlimited — ADR-0040). */
  fallback?: string | null;
}
export interface EntitlementsCatalog {
  features: EntitlementAxis[];
  limits: EntitlementAxis[];
}

/** A plan's entitlements blob ({features, limits}); a null limit = unlimited. */
export interface PlanEntitlements {
  features: Record<string, boolean>;
  limits: Record<string, number | null>;
}
export interface Plan {
  id: string;
  key: string;
  name: string;
  description: string | null;
  stripe_price_id: string | null; // Stripe Price ID for the MONTHLY cadence (Stripe-managed)
  stripe_price_year_id: string | null; // Stripe Price ID for the ANNUAL cadence (null = none)
  stripe_product_id: string | null; // the provisioned Stripe Product (null = not synced)
  entitlements: PlanEntitlements;
  public: boolean;
  active: boolean;
  sort_order: number;
  price_cents: number | null; // MONTHLY price
  price_year_cents: number | null; // optional ANNUAL total (null = no annual offer)
  currency: string;
}
export interface PlanInput {
  key: string;
  name: string;
  description?: string | null;
  entitlements: PlanEntitlements;
  public?: boolean;
  active?: boolean;
  sort_order?: number;
  price_cents?: number | null;
  price_year_cents?: number | null;
  currency?: string;
}

/** Result of a Tray→Stripe catalog sync (ADR-0041 Fase 1): the resolved ids + action log. */
export interface PlanStripeSync {
  product_id: string;
  price_id: string | null;
  price_year_id: string | null;
  actions: string[];
}

/** A tenant's subscription — carries the entitlements SNAPSHOT (what the resolver reads). */
export interface Subscription {
  id: string;
  tenant_id: string;
  plan_id: string | null;
  plan_key: string | null;
  source: string;
  status: string;
  billing_period: string; // the chosen cadence ("month" | "year")
  entitlements_snapshot: PlanEntitlements;
  overrides: Record<string, unknown>;
  // Organization-care fields (ADR-0041 §13). NULL for a comp/pre-Stripe sub.
  created_at: string;
  updated_at: string;
  current_period_end: string | null;
  past_due_at: string | null;
  cancel_at_period_end: boolean;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_dashboard_url: string | null;
  billing_suspended: boolean;
  grace_days: number;
}

/** One Stripe payment record (incasso) for the CC panel — NOT a fiscal invoice (the
 * gestionale emits those). Amounts in MAJOR units. */
export interface BillingPayment {
  number: string | null;
  status: string | null;
  currency: string;
  subtotal: number;
  tax: number;
  total: number;
  amount_paid: number;
  period_start: string | null;
  period_end: string | null;
  created: string | null;
  paid_at: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

export interface BillingPaymentsResponse {
  stripe: boolean;
  payments: BillingPayment[];
  error?: string;
}

/** One subscription-change event in the CC timeline (from the durable `subscription_events`). */
export interface BillingTimelineEntry {
  event_type: string;
  actor: string | null;
  detail: Record<string, unknown> | null;
  occurred_at: string;
}

/** A platform admin in the management list (F2.1). */
export interface PlatformAdminRow {
  id: string;
  email: string;
  full_name: string | null;
  state: "active" | "disabled";
  totp_enabled: boolean;
  last_login_at: string | null;
  created_at: string;
}

/** Inject an explicit Bearer (the forced-setup token), overriding the store token. */
function bearerInit(bearer?: string): RequestInit {
  return bearer ? { headers: { Authorization: `Bearer ${bearer}` } } : {};
}

export interface PlatformTotpStatus {
  enabled: boolean;
  recovery_codes_remaining: number;
  required: boolean;
}
export interface PlatformTotpBegin {
  secret: string;
  otpauth_uri: string;
}
export interface PlatformTotpConfirmed {
  recovery_codes: string[];
  token: string;
}

/** Paginated envelope mirroring the backend `Page[T]`. */
export interface PlatformPage<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

/** A control-plane audit row (ADR-0036 F2.4) — the superadmin's own actions, enriched
 * with the actor email + the target tenant slug. */
export interface PlatformAuditEntry {
  audit_id: number;
  event_type: string;
  action: string;
  outcome: string;
  actor_id: string | null;
  actor_email: string | null;
  target_type: string | null;
  target_id: string | null;
  target_slug: string | null;
  details: Record<string, unknown> | null;
  occurred_at: string;
}

/** The control-plane event vocabulary — mirrors the backend `PLATFORM_EVENT_TYPES`. */
export const PLATFORM_EVENT_TYPES = [
  "tenant_provisioned",
  "tenant_suspended",
  "tenant_active",
  "tenant_deprovisioned",
  "impersonation_started",
  "impersonation_elevated",
] as const;

export interface TenantContext {
  /** "default" = apex/public → login is legit; "platform" = the `platform.<base>`
   * control-plane host → serve the superadmin plane; "onboarding" = the `app.<base>`
   * host → serve the anonymous self-serve signup plane (ADR-0041); "tenant" = real
   * active subdomain; "suspended" = a real tenant that's suspended → "account
   * sospeso" page; "unknown" = a tenant subdomain that doesn't resolve → 404. */
  status: "default" | "platform" | "onboarding" | "tenant" | "unknown" | "suspended";
  slug: string | null;
  name: string | null;
  /** Where "Accedi con Account Tray" points (ADR-0077 S2) — set ONLY on a
   * subdomain-resolved tenant host (the SaaS double door). Absent on-prem/apex. */
  central_login_url?: string | null;
  /** The per-workspace local-door toggle (ADR-0077 S3): false ⇒ the login page
   * hides the username+password form (every operator is an Account Tray). The
   * backend fails OPEN (true) so a broken read never hides the only door. */
  local_login_enabled?: boolean;
}

/** Is THIS browser on the `platform.<base>` control-plane host? Synchronous (no
 * probe) so `router.tsx` can decide the route tree at build time — the bare
 * subdomain serves the superadmin plane, not the operator app (ADR-0036 Fase B).
 * Mirrors the backend `resolver.is_platform_host` (first host label === "platform"). */
export function isPlatformHost(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname.split(".")[0] === "platform";
}

/** Is THIS browser on the `app.<base>` self-serve onboarding host? Synchronous
 * (mirrors {@link isPlatformHost}) so `router.tsx` serves ONLY the signup plane on
 * the bare subdomain. Mirrors the backend `resolver.is_onboarding_host` (first host
 * label === "app", `ONBOARDING_SUBDOMAIN`). */
export function isOnboardingHost(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname.split(".")[0] === "app";
}

/** Public (unauthenticated) probe: does THIS host map to a real tenant? Used by
 * the `TenantGate` before any login renders. Fail-OPEN on any error (treat as
 * the default tenant) so a backend hiccup never locks the whole app out. */
export async function fetchTenantContext(): Promise<TenantContext> {
  try {
    const resp = await fetch(BASE + "/tenant-context", {
      headers: { "Content-Type": "application/json" },
    });
    if (!resp.ok) return { status: "default", slug: null, name: null };
    return (await resp.json()) as TenantContext;
  } catch {
    return { status: "default", slug: null, name: null };
  }
}

/** Coarse environment label for the shell badge (dev vs prod), from the host. */
export function platformEnvLabel(): "DEV" | "PROD" {
  if (typeof window === "undefined") return "PROD";
  const h = window.location.hostname;
  const dev =
    h === "localhost" ||
    h.endsWith(".localhost") ||
    h.endsWith(".local") ||
    /^(127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(h);
  return dev ? "DEV" : "PROD";
}
